import { useAction, useAtom } from '@reatom/npm-react'
import { Controller } from 'react-hook-form'
import styled, { css } from 'styled-components'

import {
  Button,
  MagnifyingGlassIcon,
  Typography,
  useHapticFeedback,
} from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { useTokenAddressSearchForm } from '@root/shared/form-control'
import { useTranslation } from '@root/shared/localization'
import { mediaMinTablet } from '@root/shared/ui-kit'

import type { CheckCurrencyReatom } from '@root/features/premium/check-currency/atoms'

type Mode = 'home' | 'currency'

type Props = {
  model: CheckCurrencyReatom
  mode: Mode
}

export const CheckCurrencyForm: React.FC<Props> = ({ model, mode }) => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()
  const [network] = useAtom(selectedNetworkAtom)

  const [requestState] = useAtom(model.checkCurrencyStateAtom)
  const checkCurrencyAction = useAction(model.checkCurrencyAction)

  const { control, formState, handleSubmitInternal, queryFieldRules } =
    useTokenAddressSearchForm({
      isValidAddress: Network.validateAddress(network),
      isLoading: requestState.loading,
      onBeforeSubmit: () => haptics.impactOccurred('light'),
      onSubmit: (address) => checkCurrencyAction({ address }),
    })

  const renderError = () => {
    if (!requestState.error) {
      return undefined
    }
    if (requestState.error._tag === 'CurrencyNotFoundError') {
      return <Error>{t('premium.search.error.currency-not-found')}</Error>
    }
    return <Error>{t('common.something_went_wrong_try_again')}</Error>
  }

  // TODO: Apply a border gradient and implement a fade-out effect when Token Check is clicked.

  return (
    <Root $mode={mode}>
      <Form onSubmit={handleSubmitInternal}>
        <SearchInput>
          <MagnifyingGlass
            size={20}
            color={formState.isValid ? '#fff' : 'var(--text-8)'}
          />
          <Controller
            name="query"
            control={control}
            rules={queryFieldRules}
            render={({ field }) => (
              <Input
                required
                placeholder={t('premium.search.title')}
                {...field}
              />
            )}
          />
        </SearchInput>
        <ButtonSearch
          busy={requestState.loading}
          disabled={!formState.isValid}
          type="submit"
          size="s"
          variant="secondary"
          radius="rounded"
        >
          {t('common.check')}
        </ButtonSearch>
      </Form>
      {renderError()}
    </Root>
  )
}

const Root = styled.div<{ $mode: Mode }>`
  padding: ${(props) => (props.$mode === 'home' ? '12px 0 18px' : 'unset')};
  position: relative;
`

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 78px;
  gap: 8px;
`

const Error = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'p',
})`
  color: var(--error-9);
  text-align: center;
  position: absolute;
  width: 100%;
  top: 57px;
  left: 0;

  ${mediaMinTablet(css`
    top: 44px;
  `)}
`

const Input = styled.input`
  color: var(--text-12);

  display: flex;
  justify-content: start;
  align-items: center;

  background: transparent;
  border: none;
  line-height: normal;

  flex: 1;
  height: 100%;
  font-size: var(--typography-body-2-regular-font-size);
  font-weight: var(--typography-body-2-regular-font-weight);
  width: 100%;
  font-style: italic;

  &::placeholder {
    color: var(--text-7);
    transition: color ${({ theme }) => theme.transition.default};
  }

  ${mediaMinTablet(css`
    font-style: normal;
  `)}
`

const MagnifyingGlass = styled(MagnifyingGlassIcon)`
  flex-shrink: 0;
`

const ButtonSearch = styled(Button)`
  color: var(--text-9);
  border-radius: 10px;
  border: 1px solid transparent;
  background: var(--backgroundA-2);
  padding-inline: 15px;
  font-weight: var(--typography-body-4-semi-bold-font-weight);
  font-size: var(--typography-body-4-semi-bold-font-size);
  line-height: var(--typography-body-4-semi-bold-line-height);

  &:disabled {
    border: 1px solid var(--backgroundA-3);
    color: var(--text-7);
    background: transparent;
  }

  &:hover:not(:disabled) {
    color: var(--text-9);
    background: var(--backgroundA-4);
  }
`

const SearchInput = styled.label`
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--backgroundA-3);
  background: rgba(25, 25, 25, 0.6);

  display: flex;
  align-items: center;
  justify-content: start;
  line-height: 1;

  padding: 0 12px;
  font-size: var(--typography-body-2-semi-bold-font-size);
  font-weight: var(--typography-body-2-semi-bold-font-weight);

  gap: 8px;

  ${mediaMinTablet(css`
    gap: 6px;
    background: var(--appearance-1);
  `)}
`
